import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Pause, Play, X, FileVideo, CheckCircle2, AlertTriangle, HardDrive, Wifi, RefreshCw, CopyPlus, Youtube, Link2, ListVideo, Bot, CloudUpload, Database } from "lucide-react";
import { api, formatApiError, fmtBytes } from "../lib/api";
import { fileFingerprint } from "../lib/fileHash";
import { useAuth } from "../context/AuthContext";
import { fmtStorage } from "../lib/planStyles";
import { GlassCard, PageHeader, Spinner } from "../components/common";
import { DomainGate } from "../components/DomainGate";
import { useLang } from "../context/LanguageContext";
import { TutorialHint } from "../components/TutorialHint";

const fmtSpeed = (bps) => (bps >= 1e9 ? `${(bps / 1e9).toFixed(1)} GB/s` : bps >= 1e6 ? `${(bps / 1e6).toFixed(1)} MB/s` : `${Math.max(0, bps / 1024).toFixed(0)} KB/s`);
const fmtEta = (secs) => {
  if (!Number.isFinite(secs) || secs <= 0) return "—";
  if (secs < 60) return `${Math.ceil(secs)}s`;
  if (secs < 3600) return `${Math.floor(secs / 60)}min ${Math.ceil(secs % 60)}s`;
  return `${Math.floor(secs / 3600)}h ${Math.floor((secs % 3600) / 60)}min`;
};

const STATUS_LABEL = {
  queued: { txt: "Na fila", cls: "text-slate-400" },
  uploading: { txt: "Enviando", cls: "text-cyan-300" },
  paused: { txt: "Pausado", cls: "text-amber-300" },
  done: { txt: "Concluído", cls: "text-emerald-300" },
  duplicate: { txt: "Duplicado", cls: "text-amber-300" },
  canceled: { txt: "Cancelado", cls: "text-slate-500" },
  error: { txt: "Erro", cls: "text-rose-400" },
};

const SRV_STATUS = {
  queued: { txt: "Na fila", cls: "text-amber-300 border-amber-400/30 bg-amber-400/10" },
  running: { txt: "Processando", cls: "text-cyan-300 border-cyan-400/30 bg-cyan-400/10" },
  done: { txt: "Concluído", cls: "text-emerald-300 border-emerald-400/30 bg-emerald-400/10" },
  failed: { txt: "Erro", cls: "text-rose-300 border-rose-400/30 bg-rose-400/10" },
  canceled: { txt: "Cancelado", cls: "text-slate-400 border-slate-600 bg-slate-800/40" },
};

export default function Uploads() {
  const { user } = useAuth();
  const { t } = useLang();
  const [bots, setBots] = useState([]);
  const [botId, setBotId] = useState("");
  const [dest, setDest] = useState("account");
  const [folder, setFolder] = useState("");
  const [folderOptions, setFolderOptions] = useState([]);
  const [ownBotId, setOwnBotId] = useState("");
  const [ytUrl, setYtUrl] = useState("");
  const [ytBusy, setYtBusy] = useState(false);
  const [ytFolder, setYtFolder] = useState("");
  const [m3uUrl, setM3uUrl] = useState("");
  const [m3uKind, setM3uKind] = useState("all");
  const [m3uBusy, setM3uBusy] = useState(false);
  const [quota, setQuota] = useState(null);
  const [srvJobs, setSrvJobs] = useState({});
  const [items, setItems] = useState([]);
  const [resumable, setResumable] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const controls = useRef({});
  const speedRefs = useRef({});
  const inputRef = useRef(null);
  const lidSeq = useRef(1);

  const setItem = useCallback((lid, patch) => {
    setItems((prev) => prev.map((it) => (it.lid === lid ? { ...it, ...patch } : it)));
  }, []);

  const loadQuota = useCallback(() => {
    if (!botId) return;
    api.get("/uploads/quota", { params: { bot_id: botId } }).then((r) => setQuota(r.data)).catch(() => setQuota(null));
  }, [botId]);

  useEffect(() => {
    api.get("/folders/tree").then((r) => setFolderOptions((r.data.folders || []).map((f) => f.name))).catch(() => {});
    api.get("/bots").then((r) => {
      const list = r.data || [];
      setBots(list);
      const own = list.find((b) => b.owner_id === user.id) || list[0];
      if (own) {
        setBotId(own.id);
        if (own.owner_id === user.id) setOwnBotId(own.id);
      }
    }).catch(() => {});
    api.get("/uploads/active").then((r) => setResumable(r.data || [])).catch(() => {});
    // carrega jobs ativos do servidor (importação/migração/lista) para acompanhar aqui em Uploads
    api.get("/queue").then((r) => {
      const actives = (r.data.items || []).filter((i) => i.status === "queued" || i.status === "running" || i.status === "paused");
      if (actives.length) setSrvJobs((prev) => ({ ...prev, ...Object.fromEntries(actives.map((i) => [i.id, i])) }));
    }).catch(() => {});
  }, [user.id]);

  useEffect(() => { loadQuota(); }, [loadQuota]);

  const trackSrvJob = useCallback((job) => {
    setSrvJobs((prev) => ({ ...prev, [job.id]: job }));
  }, []);

  useEffect(() => {
    const active = Object.values(srvJobs).filter((j) => j.status === "queued" || j.status === "running" || j.status === "paused");
    if (!active.length) return undefined;
    const t = setInterval(() => {
      api.get("/queue").then((r) => {
        const map = Object.fromEntries((r.data.items || []).map((i) => [i.id, i]));
        setSrvJobs((prev) => {
          const next = { ...prev };
          active.forEach((j) => {
            if (map[j.id]) next[j.id] = { ...next[j.id], ...map[j.id] };
          });
          return next;
        });
      }).catch(() => {});
    }, 4000);
    return () => clearInterval(t);
  }, [srvJobs]);

  const doYtLink = async () => {
    if (!ytUrl.trim() || !botId) return;
    setYtBusy(true);
    try {
      const { data: job } = await api.post("/import/yt", { url: ytUrl.trim(), version: "dual", bot_id: botId, folder: ytFolder || undefined });
      trackSrvJob({ ...job, _kind: "yt" });
      toast.success("Download iniciado no servidor — acompanhe aqui embaixo.");
      setYtUrl("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setYtBusy(false);
    }
  };

  const doM3uLink = async () => {
    if (!m3uUrl.trim()) return;
    setM3uBusy(true);
    try {
      const { data: job } = await api.post("/import/m3u-url", { url: m3uUrl.trim(), kind_filter: m3uKind });
      trackSrvJob({ ...job, _kind: "m3u" });
      toast.success("Leitura da lista iniciada — ao terminar, a subida para o acervo entra na Fila automaticamente.");
      setM3uUrl("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setM3uBusy(false);
    }
  };

  const dismissSrvJob = (id) => setSrvJobs((prev) => { const n = { ...prev }; delete n[id]; return n; });

  const startQueue = useCallback(() => {
    setItems((prev) => {
      const uploading = prev.filter((i) => i.status === "uploading").length;
      let slots = Math.max(0, 2 - uploading);
      for (const it of prev) {
        if (slots <= 0) break;
        if (it.status === "queued") {
          slots -= 1;
          setTimeout(() => uploadItemRef.current(it), 0);
          it.status = "uploading";
        }
      }
      return [...prev];
    });
  }, []);

  const uploadItemRef = useRef(null);

  uploadItemRef.current = async (item) => {
    const ctl = { paused: false, canceled: false, abort: null };
    controls.current[item.lid] = ctl;
    speedRefs.current[item.lid] = { last: item.progress || 0, t: Date.now(), speed: 0, lastUi: 0 };
    try {
      const fp = await fileFingerprint(item.file);
      const { data: sess } = await api.post("/uploads/init", { bot_id: botId, filename: item.name, size_bytes: item.size, folder: folder || null, file_hash: fp || undefined });
      if (sess.duplicate) {
        setItem(item.lid, { status: "done", progress: item.size, speed: 0, eta: 0, title: sess.title });
        toast.success(`"${sess.title}" — ficheiro idêntico já estava na nuvem: reutilizado (0 B gastos)`);
        loadQuota();
        return;
      }
      if (sess.resumed) toast.info(`"${item.name}" continuando de onde parou`);
      setItem(item.lid, { sessionId: sess.session_id, status: "uploading", error: null });
      const totalParts = Math.ceil(item.size / sess.chunk_size);
      const partsDone = new Set(sess.parts_done);
      let done = sess.bytes_received;
      setItem(item.lid, { progress: done, totalParts });
      for (let n = 1; n <= totalParts; n++) {
        while (ctl.paused && !ctl.canceled) await new Promise((r) => setTimeout(r, 400));
        if (ctl.canceled) return;
        if (partsDone.has(n)) continue;
        const start = (n - 1) * sess.chunk_size;
        const blob = item.file.slice(start, start + sess.chunk_size);
        ctl.abort = new AbortController();
        let loaded = 0;
        await api.put(`/uploads/${sess.session_id}/part/${n}`, blob, {
          headers: { "Content-Type": "application/octet-stream" },
          signal: ctl.abort.signal,
          onUploadProgress: (e) => {
            loaded = e.loaded;
            const total = done + e.loaded;
            const sr = speedRefs.current[item.lid];
            const now = Date.now();
            const dt = (now - sr.t) / 1000;
            if (dt >= 0.5) {
              const inst = (total - sr.last) / dt;
              sr.speed = sr.speed ? sr.speed * 0.6 + inst * 0.4 : inst;
              sr.last = total;
              sr.t = now;
            }
            if (now - sr.lastUi > 400 || total >= item.size) {
              sr.lastUi = now;
              const eta = sr.speed > 0 ? (item.size - total) / sr.speed : Infinity;
              setItem(item.lid, { progress: total, speed: sr.speed, eta });
            }
          },
        });
        done += loaded || blob.size;
        partsDone.add(n);
        setItem(item.lid, { progress: done, partsDone: new Set(partsDone) });
      }
      const { data: fin } = await api.post(`/uploads/${sess.session_id}/finish`);
      setItem(item.lid, { status: "done", progress: item.size, speed: 0, eta: 0, title: fin.title });
      toast.success(`"${fin.title}" entrou no acervo!`);
      loadQuota();
      api.get("/uploads/active").then((r) => setResumable(r.data || [])).catch(() => {});
    } catch (e) {
      if (ctl.canceled) return;
      if (e.code === "ERR_CANCELED" || ctl.paused) {
        setItem(item.lid, { status: "paused", speed: 0 });
        return;
      }
      const st = e.response?.status;
      if (st === 409) {
        setItem(item.lid, { status: "duplicate", error: e.response?.data?.detail || "Arquivo já existe no acervo", speed: 0 });
        return;
      }
      setItem(item.lid, { status: "paused", error: formatApiError(e), speed: 0 });
    } finally {
      delete controls.current[item.lid];
      startQueue();
    }
  };

  const addFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    if (!botId) {
      toast.error("Selecione um bot primeiro");
      return;
    }
    const news = files.map((f) => ({
      lid: lidSeq.current++, file: f, name: f.name, size: f.size,
      status: "queued", progress: 0, speed: 0, eta: Infinity, error: null,
      partsDone: new Set(), totalParts: 0, sessionId: null,
    }));
    setItems((prev) => [...prev, ...news]);
    setTimeout(startQueue, 50);
  };

  const pauseItem = (lid) => {
    const ctl = controls.current[lid];
    if (ctl) {
      ctl.paused = true;
      ctl.abort?.abort();
    }
    setItem(lid, { status: "paused", speed: 0 });
  };

  const resumeItem = (lid) => {
    setItem(lid, { status: "queued", error: null });
    setTimeout(startQueue, 50);
  };

  const cancelItem = async (lid) => {
    const it = items.find((i) => i.lid === lid);
    const ctl = controls.current[lid];
    if (ctl) {
      ctl.canceled = true;
      ctl.abort?.abort();
    }
    if (it?.sessionId) {
      try { await api.delete(`/uploads/${it.sessionId}`); } catch (e) { /* noop */ }
    }
    setItem(lid, { status: "canceled", speed: 0 });
  };

  const dismissItem = (lid) => setItems((prev) => prev.filter((i) => i.lid !== lid));

  const activeCount = items.filter((i) => i.status === "uploading" || i.status === "queued").length;
  const availTxt = quota ? (quota.unlimited ? "Ilimitado" : fmtStorage(Math.round((quota.available_bytes / 1e9) * 100) / 100)) : "…";
  const pendingBytes = items
    .filter((i) => ["queued", "uploading", "paused"].includes(i.status))
    .reduce((acc, i) => acc + Math.max(0, i.size - i.progress), 0);
  const afterBytes = quota && !quota.unlimited ? quota.available_bytes - pendingBytes : null;
  const fmtGb2 = (b) => fmtStorage(Math.round((b / 1e9) * 100) / 100);

  return (
    <div className="space-y-6" data-testid="uploads-page">
      <PageHeader eyebrow={t("u_eyebrow")} title={t("uploads")} subtitle={t("u_sub")} />
      <DomainGate />
      <TutorialHint
        id="uploads"
        title={t("u_tutorial")}
        steps={[
          t("u_tut1"),
          t("u_tut2"),
          t("u_tut3"),
          t("u_tut4"),
          t("u_tut5"),
        ]}
      />

      <div className="rounded-xl border border-cyan-400/25 bg-cyan-400/5 px-5 py-4 flex items-start gap-3 anim-fade-up" data-testid="internet-notice">
        <Wifi size={16} className="text-cyan-400 shrink-0 mt-0.5" />
        <p className="text-xs text-slate-300 leading-relaxed">
          <b className="text-cyan-300">Ficheiro do seu aparelho</b> = sobe pela <b>SUA internet</b> (na velocidade do seu operador) e a aba precisa ficar aberta — se fechar ou cair, o upload pausa e continua de onde parou quando voltar e escolher o mesmo ficheiro.
          <br />
          <b className="text-emerald-300">Link de vídeo ou lista M3U</b> = quem baixa é o <b>nosso servidor</b>, na velocidade do datacenter: <b>pode fechar a página</b> e voltar depois — o trabalho continua e aparece na fila de Importações.
        </p>
      </div>

      <GlassCard className="p-6 space-y-5 anim-fade-up" data-testid="upload-card">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-48">
            <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">Enviar para</label>
            <select data-testid="upload-bot-select" className="input-dark w-full rounded-xl px-4 py-3 text-sm" value={dest} onChange={(e) => {
              const v = e.target.value;
              setDest(v);
              setBotId(v === "account" ? (ownBotId || bots[0]?.id || "") : v);
            }}>
              <option value="account">📁 Pasta da minha conta (raiz na nuvem)</option>
              {bots.filter((b) => b.owner_id === user.id || user.role === "super_admin").map((b) => <option key={b.id} value={b.id}>🤖 Bot: @{b.username}</option>)}
            </select>
            {!ownBotId && user.role !== "super_admin" && (
              <p className="text-[11px] text-amber-300/90 mt-2 flex items-center gap-1.5" data-testid="upload-no-bot-hint">
                <Bot size={12} /> Ainda sem bot do Telegram? <a href="/app/bots" className="underline font-semibold" data-testid="upload-create-bot-link">Crie aqui em 1 minuto</a> — sem bot, os uploads caem na pasta da sua conta mesmo assim.
              </p>
            )}
          </div>
          <div className="min-w-48">
            <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">Pasta de destino</label>
            <select data-testid="upload-folder-select" className="input-dark w-full rounded-xl px-4 py-3 text-sm" value={folder} onChange={(e) => setFolder(e.target.value)}>
              <option value="">🗂 Automática (Filmes/Séries)</option>
              {folderOptions.map((f) => <option key={f} value={f}>📁 {f}</option>)}
            </select>
          </div>
          <div className={`rounded-xl border px-5 py-3 ${afterBytes !== null && afterBytes < 0 ? "border-rose-400/50 bg-rose-400/5" : "border-slate-800 bg-slate-950/40"}`} data-testid="upload-quota">
            <p className="text-[9px] font-mono2 uppercase tracking-widest text-slate-500 flex items-center gap-1.5"><HardDrive size={11} /> Espaço disponível</p>
            <p className="font-display text-lg font-bold text-white mt-0.5">{availTxt}</p>
            {quota && !quota.unlimited && (
              <p className="text-[10px] text-slate-500">usado {fmtBytes(quota.storage_used_bytes)} de {quota.plan_storage_gb} GB · máx 100 GB/arquivo</p>
            )}
            {afterBytes !== null && pendingBytes > 0 && (
              <p className={`text-[10px] mt-1 font-semibold ${afterBytes < 0 ? "text-rose-400" : "text-emerald-300"}`} data-testid="upload-quota-after">
                {afterBytes < 0
                  ? `após esses uploads: faltam ${fmtGb2(-afterBytes)} — reduza a fila ou faça upgrade`
                  : `após esses uploads: sobram ${fmtGb2(afterBytes)}`}
              </p>
            )}
          </div>
        </div>

        <div
          data-testid="upload-dropzone"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
          className={`rounded-2xl border-2 border-dashed px-6 py-12 text-center cursor-pointer transition-colors ${dragOver ? "border-cyan-400 bg-cyan-400/10" : "border-slate-700 hover:border-cyan-400/50 hover:bg-slate-900/40"}`}
        >
          <Upload size={32} className={`mx-auto mb-3 ${dragOver ? "text-cyan-300" : "text-slate-500"}`} />
          <p className="text-sm text-white font-semibold">Arraste e solte seus vídeos aqui</p>
          <p className="text-xs text-slate-500 mt-1">ou clique para escolher — vários arquivos ao mesmo tempo, até 100 GB cada</p>
          <input ref={inputRef} data-testid="upload-file-input" type="file" multiple accept="video/*,.mp4,.mkv,.avi,.m4v,.webm" className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
        </div>

        {resumable.length > 0 && (
          <div className="rounded-xl border border-amber-400/25 bg-amber-400/5 px-4 py-3" data-testid="resumable-hint">
            <p className="text-xs text-amber-200 flex items-center gap-2">
              <RefreshCw size={13} className="shrink-0" />
              {resumable.length} upload(s) interrompido(s): {resumable.map((r) => r.filename).join(", ").slice(0, 120)}.
              Selecione o mesmo arquivo novamente para continuar de onde parou.
            </p>
          </div>
        )}
      </GlassCard>

      <div className="grid md:grid-cols-2 gap-5 [&>*]:min-w-0">
        <GlassCard className="p-6 anim-fade-up" data-testid="link-video-card">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-11 h-11 rounded-xl bg-rose-400/10 border border-rose-400/25 flex items-center justify-center shrink-0">
              <Youtube size={20} className="text-rose-400" />
            </div>
            <div>
              <h3 className="font-display text-white font-semibold text-sm">Link de vídeo (o servidor baixa)</h3>
              <p className="text-[11px] text-slate-500">YouTube, sites de filme, link direto .mp4 — internet do servidor, pode fechar a página</p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <input data-testid="link-video-input" className="input-dark flex-1 min-w-0 rounded-xl px-4 py-3 text-sm font-mono2" placeholder="https://youtube.com/watch?v=... ou link direto do vídeo" value={ytUrl} onChange={(e) => setYtUrl(e.target.value)} />
            <select data-testid="link-video-folder" className="input-dark rounded-xl px-4 py-3 text-sm sm:max-w-52" value={ytFolder} onChange={(e) => setYtFolder(e.target.value)} title="Pasta de destino do vídeo">
              <option value="">🗂 Automática (Filmes/Séries)</option>
              {folderOptions.map((f) => <option key={f} value={f}>📁 {f}</option>)}
            </select>
            <button data-testid="link-video-btn" onClick={doYtLink} disabled={ytBusy || !ytUrl.trim()} className="btn-neon px-5 py-3 rounded-xl text-sm flex items-center justify-center gap-2 shrink-0">
              {ytBusy ? <Spinner /> : <><Link2 size={15} /> Baixar no servidor</>}
            </button>
          </div>
        </GlassCard>

        <GlassCard className="p-6 anim-fade-up" data-testid="link-m3u-card">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-11 h-11 rounded-xl bg-violet-400/10 border border-violet-400/25 flex items-center justify-center shrink-0">
              <ListVideo size={20} className="text-violet-300" />
            </div>
            <div>
              <h3 className="font-display text-white font-semibold text-sm">Lista M3U (catálogo inteiro)</h3>
              <p className="text-[11px] text-slate-500">o servidor lê a lista; a migração para a nuvem é disparada em Importações</p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <input data-testid="link-m3u-input" className="input-dark flex-1 min-w-0 rounded-xl px-4 py-3 text-sm font-mono2" placeholder="http://fornecedor.com/get.php?username=...&type=m3u_plus&output=mpegts" value={m3uUrl} onChange={(e) => setM3uUrl(e.target.value)} />
            <select data-testid="link-m3u-kind" className="input-dark rounded-xl px-4 py-3 text-sm shrink-0" value={m3uKind} onChange={(e) => setM3uKind(e.target.value)} title="O que ler da lista">
              <option value="all">📦 Tudo</option>
              <option value="movie">🎬 Só filmes</option>
              <option value="series">📺 Só séries</option>
              <option value="live">📡 Só canais</option>
            </select>
            <button data-testid="link-m3u-btn" onClick={doM3uLink} disabled={m3uBusy || !m3uUrl.trim()} className="btn-neon px-5 py-3 rounded-xl text-sm flex items-center justify-center gap-2 shrink-0">
              {m3uBusy ? <Spinner /> : <><ListVideo size={15} /> Ler lista</>}
            </button>
          </div>
          <p className="text-[10px] text-slate-600 mt-2">Para escolher categorias da lista ou agendar importações automáticas, use o menu <b className="text-slate-400">Agendadas</b>.</p>
        </GlassCard>
      </div>

      {Object.keys(srvJobs).length > 0 && (
        <GlassCard className="p-6 anim-fade-up" data-testid="srv-jobs-card">
          <h3 className="font-display text-white font-semibold text-sm mb-4 flex items-center gap-2">
            <RefreshCw size={15} className="text-cyan-400" /> Downloads do servidor em andamento
          </h3>
          <div className="space-y-3">
            {Object.values(srvJobs).map((j) => {
              const st = SRV_STATUS[j.status] || SRV_STATUS.queued;
              const running = j.status === "queued" || j.status === "running";
              const pctMatch = /\((\d+)%\)/.exec(j.current || "");
              const pct = pctMatch ? Number(pctMatch[1]) : null;
              return (
                <div key={j.id} className="rounded-xl border border-slate-800/60 bg-slate-950/40 p-4" data-testid={`srvjob-${j.id}`}>
                  <div className="flex flex-wrap items-center gap-3">
                    <div className={`w-9 h-9 rounded-xl border flex items-center justify-center shrink-0 ${j._kind === "m3u" || j.kind === "m3u" ? "bg-violet-400/10 border-violet-400/25" : j.kind === "migrate" ? "bg-cyan-400/10 border-cyan-400/25" : j.kind === "import" ? "bg-emerald-400/10 border-emerald-400/25" : "bg-rose-400/10 border-rose-400/25"}`}>
                      {j._kind === "m3u" || j.kind === "m3u" ? <ListVideo size={16} className="text-violet-300" /> : j.kind === "migrate" ? <Database size={16} className="text-cyan-300" /> : j.kind === "import" ? <CloudUpload size={16} className="text-emerald-300" /> : <Youtube size={16} className="text-rose-400" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-slate-500 font-mono2 truncate" title={j.source_url || j.name}>{j.source_url || j.name}</p>
                      {j.label && j.kind !== "m3u" && j.kind !== "yt" && <p className="text-[10px] font-mono2 uppercase tracking-widest text-slate-500">{j.label}</p>}
                      <p className="text-sm text-white font-semibold mt-0.5 truncate" data-testid={`srvjob-current-${j.id}`}>{j.current || "…"}</p>
                    </div>
                    <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md border text-[10px] font-mono2 font-semibold uppercase tracking-wider ${st.cls}`} data-testid={`srvjob-status-${j.id}`}>
                      {running && <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />} {st.txt}
                    </span>
                    {!running && (
                      <button data-testid={`srvjob-dismiss-${j.id}`} onClick={() => dismissSrvJob(j.id)} title="Fechar" className="p-1.5 rounded-lg btn-ghost-dark text-slate-500"><X size={13} /></button>
                    )}
                  </div>
                  <div className="mt-3 h-2 rounded-full bg-slate-800 overflow-hidden" data-testid={`srvjob-bar-${j.id}`}>
                    <div className={`h-full rounded-full transition-all duration-500 ${j.status === "failed" ? "bg-rose-400 w-full" : j.status === "done" ? "bg-emerald-400 w-full" : "bg-gradient-to-r from-cyan-400 to-emerald-400"}`}
                      style={running ? (pct != null ? { width: `${pct}%` } : { width: "40%", animation: "pulse 1.6s ease-in-out infinite" }) : {}} />
                  </div>
                  {j.status === "failed" && (
                    <div className="mt-3 rounded-xl border border-rose-400/40 bg-rose-400/10 px-4 py-3" data-testid={`srvjob-error-${j.id}`}>
                      <p className="text-xs text-rose-200 leading-relaxed flex items-start gap-2">
                        <AlertTriangle size={14} className="text-rose-400 shrink-0 mt-0.5" />
                        <span><b>Não foi possível concluir:</b> {j.error || "erro desconhecido"}</span>
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </GlassCard>
      )}

      {items.length > 0 && (
        <GlassCard className="p-6 anim-fade-up" data-testid="upload-queue">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-white font-semibold text-sm flex items-center gap-2"><CopyPlus size={15} className="text-cyan-400" /> Fila de uploads</h3>
            <span className="text-[10px] font-mono2 text-slate-500" data-testid="upload-queue-count">{activeCount} ativo(s) · {items.length} total</span>
          </div>
          <div className="space-y-3">
            {items.map((it) => {
              const pct = it.size ? Math.min(100, (it.progress / it.size) * 100) : 0;
              const s = STATUS_LABEL[it.status] || STATUS_LABEL.queued;
              return (
                <div key={it.lid} className="rounded-xl border border-slate-800/60 bg-slate-950/40 p-4" data-testid={`upload-item-${it.lid}`}>
                  <div className="flex flex-wrap items-center gap-3">
                    <FileVideo size={16} className="text-slate-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white font-semibold truncate">{it.title || it.name}</p>
                      <p className="text-[10px] font-mono2 text-slate-500 mt-0.5">
                        {fmtBytes(it.size)} · <span className={s.cls} data-testid={`upload-status-${it.lid}`}>{s.txt}</span>
                        {it.status === "uploading" && (
                          <span data-testid={`upload-speed-${it.lid}`}> · {fmtSpeed(it.speed)} · falta {fmtEta(it.eta)}</span>
                        )}
                      </p>
                      {it.error && <p className="text-[10px] text-amber-300/90 mt-0.5" data-testid={`upload-error-${it.lid}`}>{it.error}</p>}
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {it.status === "uploading" && (
                        <button data-testid={`upload-pause-${it.lid}`} onClick={() => pauseItem(it.lid)} title="Pausar" className="p-2 rounded-lg btn-ghost-dark text-amber-300"><Pause size={14} /></button>
                      )}
                      {it.status === "paused" && (
                        <button data-testid={`upload-resume-${it.lid}`} onClick={() => resumeItem(it.lid)} title="Continuar" className="p-2 rounded-lg btn-ghost-dark text-emerald-300"><Play size={14} /></button>
                      )}
                      {(it.status === "uploading" || it.status === "paused" || it.status === "queued") && (
                        <button data-testid={`upload-cancel-${it.lid}`} onClick={() => cancelItem(it.lid)} title="Cancelar" className="p-2 rounded-lg btn-ghost-dark text-rose-400"><X size={14} /></button>
                      )}
                      {(it.status === "done") && <CheckCircle2 size={16} className="text-emerald-400" />}
                      {(it.status === "duplicate") && <AlertTriangle size={16} className="text-amber-400" />}
                      {(it.status === "done" || it.status === "duplicate" || it.status === "canceled") && (
                        <button data-testid={`upload-dismiss-${it.lid}`} onClick={() => dismissItem(it.lid)} title="Remover da lista" className="p-2 rounded-lg btn-ghost-dark text-slate-500"><X size={14} /></button>
                      )}
                    </div>
                  </div>
                  <div className="mt-3 h-2 rounded-full bg-slate-800 overflow-hidden">
                    <div
                      data-testid={`upload-progress-${it.lid}`}
                      className={`h-full rounded-full transition-all duration-300 ${it.status === "done" ? "bg-emerald-400" : it.status === "duplicate" || it.status === "error" ? "bg-amber-400" : "bg-gradient-to-r from-cyan-400 to-emerald-400"}`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <p className="text-[10px] font-mono2 text-slate-500 mt-1.5">{pct.toFixed(0)}% · {fmtBytes(it.progress)} de {fmtBytes(it.size)}{it.totalParts ? ` · parte ${it.partsDone.size}/${it.totalParts}` : ""}</p>
                </div>
              );
            })}
          </div>
        </GlassCard>
      )}
    </div>
  );
}
